import React, { useEffect, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as ActionCreators from '../../redux/action';
import InvestmentsTableSC, {
  PaginationWrapSC,
  AdditionalTopSC,
  EmptySC,
  FilterRowSC,
} from './investments-table.styled';
import { BodyText, SmallText } from '../typography';
import InvestmentItem from './investment-item';
import dlt from '../../utils/default-lang';
import { FormattedMessage } from 'react-intl';
import useWindowSize from '../../utils/screen-size';
import { BP_TABLET_MIN } from '../../assets/styles/breakpoints';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import Button from '../ui/button';
import { ROUTE_INVESTMENTS } from '../../constants';
import AutomaticSaleModal from './automatic-sale-modal';
import InvestmentsFilterSelect from '../table/investments-filter-select';
import ALL_STATUSES from '../../types/investments-types';
import Pagination from '../ui/pagination';
import { TableHeaderSorter } from '../ui';

const defaultItemsPerPage = 10;
const defaultSorter = { loanDueAt: 'desc' };

function getStatusForTransaction(item) {
  if (item.isSold) {
    return 'sold';
  }

  switch (item.loan?.status?.id) {
    case 2:
    case 4:
      return 'paying';
    case 3:
      return item.loan.daysInArrears >= 120 ? 'high-delay' : 'medium-delay';
    // return "late"
    case 5:
    case 6:
    case 7:
    case 8:
      return 'paid';
    default:
      return null;
  }
}

const _InvestmentsTable = ({
  maxRows,
  hasPagination,
  hasAdditionalTop,
  hasLinkOnAll,
  history,
  getInvestorInvestments,
  investments,
  hasSelectAll,
  hasFilter,
  allowStatuses = true,
  itemsPerPage = defaultItemsPerPage,
}) => {
  const screenSize = useWindowSize();
  const isTablet = screenSize.width < BP_TABLET_MIN;
  const [formattedData, setFormattedData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [stateFilter, setStateFilter] = useState(ALL_STATUSES);
  const [sorter, setSorter] = useState(defaultSorter);

  const fetch = useCallback(
    ({
      page,
      perPage,
      filters = {
        statuses: allowStatuses ? ALL_STATUSES : undefined,
        order_by: Object.keys(defaultSorter)[0],
        order_direction: Object.values(defaultSorter)[0],
      },
    }) => {
      let formattedData = {
        per_page: perPage,
        page,
        ...filters,
      };

      const sendTablePagination = async () => {
        await getInvestorInvestments({ data: formattedData, onSuccess, onBadResponse });
        setIsLoading(true);
      };

      const onBadResponse = () => {
        setIsLoading(true);
        formattedData = {
          page: 1,
          ...formattedData,
        };
        setTimeout(() => {
          sendTablePagination();
        }, 10000);
      };

      const onSuccess = () => {
        setIsLoading(false);
      };

      sendTablePagination();
    },
    [allowStatuses, getInvestorInvestments]
  );

  useEffect(() => {
    history.location.state?.sorter && setSorter(history.location.state.sorter);
    history.location.state?.stateFilter && setStateFilter(history.location.state.stateFilter);

    fetch({
      page: history.location.state?.tablePage || 1,
      perPage: history.location.state?.tableSize || itemsPerPage,
      filters: {
        statuses: history.location.state?.stateFilter || undefined,
        order_by: history.location.state?.sorter
          ? Object.keys(history.location.state?.sorter)[0]
          : Object.keys(defaultSorter)[0],
        order_direction: history.location.state?.sorter
          ? Object.values(history.location.state?.sorter)[0]
          : Object.values(defaultSorter)[0],
      },
    });
  }, [fetch, itemsPerPage, history.location]);

  useEffect(() => {
    let formattedDataArray = [];
    if (maxRows) {
      formattedDataArray = investments.items.slice(0, maxRows);
    } else {
      formattedDataArray = investments.items;
    }
    setFormattedData(formattedDataArray);
  }, [maxRows, investments]);

  const showAllLoanRepayments = () => {
    history.push(`${ROUTE_INVESTMENTS}`);
  };

  function handleStateFilter(values) {
    setStateFilter(values);
    history.replace({
      ...history.location,
      state: {
        ...history.location.state,
        tablePage: 1,
        stateFilter: values,
      },
    });
  }

  function handleTableChange(page, size) {
    history.replace({
      ...history.location,
      state: {
        ...history.location.state,
        tableSize: size,
        tablePage: page,
      },
    });
  }

  function handleTableSize(_, size) {
    history.replace({
      ...history.location,
      state: {
        ...history.location.state,
        tablePage: 1,
        tableSize: size,
      },
    });
  }

  function handleSortChange(key, direction) {
    setSorter({
      [key]: direction === 'none' ? undefined : direction,
    });

    history.replace({
      ...history.location,
      state: {
        ...history.location.state,
        tablePage: 1,
        sorter: {
          [key]: direction,
        },
      },
    });
  }

  return (
    <>
      {hasAdditionalTop ? (
        <AdditionalTopSC>
          <BodyText className="bolder">
            <FormattedMessage
              id="investor_invest_table_headline"
              defaultMessage={dlt['investor_invest_table_headline']}
            />
          </BodyText>
          {hasLinkOnAll ? (
            <Button type="LINK" onClick={() => showAllLoanRepayments()}>
              <FormattedMessage id="global_show_all" defaultMessage={dlt['global_show_all']} />
            </Button>
          ) : hasAdditionalTop && hasSelectAll ? (
            <Button type="OUTLINED" className="small-btn" onClick={() => setModalVisible(true)}>
              <FormattedMessage
                id="investor_invest_table_select_btn"
                defaultMessage={dlt['investor_invest_table_select_btn']}
              />
            </Button>
          ) : null}
        </AdditionalTopSC>
      ) : null}

      {hasFilter ? (
        <FilterRowSC>
          <InvestmentsFilterSelect onChange={handleStateFilter} value={stateFilter} />
        </FilterRowSC>
      ) : null}

      <InvestmentsTableSC>
        {isTablet ? null : (
          <thead>
            <tr>
              <TableHeaderSorter
                sortKey={'wmNumber'}
                onChange={handleSortChange}
                sortDirection={sorter['wmNumber']}
                allowSortOff={false}
              >
                <SmallText className="black-transparent">
                  <FormattedMessage
                    id="investor_investments_table_name"
                    defaultMessage={dlt['investor_investments_table_name']}
                  />
                </SmallText>
              </TableHeaderSorter>
              <TableHeaderSorter
                sortKey="amount"
                onChange={handleSortChange}
                sortDirection={sorter['amount']}
                allowSortOff={false}
              >
                <SmallText className="black-transparent">
                  <FormattedMessage
                    id="investor_investments_table_value"
                    defaultMessage={dlt['investor_investments_table_value']}
                  />
                </SmallText>
              </TableHeaderSorter>
              <TableHeaderSorter
                sortKey="paidInstallmentsCount"
                onChange={handleSortChange}
                sortDirection={sorter['paidInstallmentsCount']}
                allowSortOff={false}
              >
                <SmallText className="black-transparent">
                  <FormattedMessage
                    id="investor_investments_table_payments"
                    defaultMessage={dlt['investor_investments_table_payments']}
                  />
                </SmallText>
              </TableHeaderSorter>
              <TableHeaderSorter
                sortKey="loanDueAt"
                onChange={handleSortChange}
                sortDirection={sorter['loanDueAt']}
                allowSortOff={false}
              >
                <SmallText className="black-transparent">
                  <FormattedMessage
                    id="investor_investments_table_date"
                    defaultMessage={dlt['investor_investments_table_date']}
                  />
                </SmallText>
              </TableHeaderSorter>
              <TableHeaderSorter
                sortKey="lastPaymentDate"
                onChange={handleSortChange}
                sortDirection={sorter['lastPaymentDate']}
                allowSortOff={false}
              >
                <SmallText className="black-transparent">
                  <FormattedMessage
                    id="investor_investments_table_last_payment"
                    defaultMessage={dlt['investor_investments_table_last_payment']}
                  />
                </SmallText>
              </TableHeaderSorter>
              <th>
                <SmallText className="black-transparent">
                  <FormattedMessage
                    id="investor_investments_table_status"
                    defaultMessage={dlt['investor_investments_table_status']}
                  />
                </SmallText>
              </th>
              <th></th>
            </tr>
          </thead>
        )}

        <tbody>
          {formattedData.map((item) => {
            return (
              <InvestmentItem
                hasPagination={hasPagination}
                key={item.id}
                id={item.id}
                name={item.debtor.wmNumber}
                payments={item.loan.paidInstallmentCount}
                paymentsTotal={item.loan.totalInstallmentCount}
                amount={Number(item.amount)}
                date={item.loan.loanDueAt}
                status={getStatusForTransaction(item)}
                inArrear={item.loan.daysInArrears}
                isTablet={isTablet}
                lastPaymentDate={item.loan.lastPaymentDate}
              />
            );
          })}
        </tbody>
      </InvestmentsTableSC>
      {formattedData.length ? (
        <>
          {hasPagination ? (
            <PaginationWrapSC>
              <Pagination
                pagination={investments.pagination}
                onChange={handleTableChange}
                onPageSizeChange={handleTableSize}
                isLoading={isLoading}
              />
            </PaginationWrapSC>
          ) : null}
        </>
      ) : (
        <EmptySC>
          <div className="empty-table">
            <SmallText className="black-transparent bold">
              <FormattedMessage
                id="investor_investments_table_empty"
                defaultMessage={dlt['investor_investments_table_empty']}
              />
            </SmallText>
          </div>
        </EmptySC>
      )}
      <AutomaticSaleModal
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        onSuccess={() => setModalVisible(false)}
      />
    </>
  );
};

_InvestmentsTable.propTypes = {
  maxRows: PropTypes.number,
  hasPagination: PropTypes.bool,
  hasAdditionalTop: PropTypes.bool,
  hasFilter: PropTypes.bool,
};

_InvestmentsTable.defaultProps = {
  maxRows: null,
  hasPagination: false,
  hasAdditionalTop: false,
  hasFilter: false,
};

const mapStateToProps = (state) => {
  return {
    investments: state.investments,
  };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      getInvestorInvestments: ActionCreators.getInvestorInvestments,
    },
    dispatch
  );
};

const InvestmentsTable = withRouter(
  connect(mapStateToProps, mapDispatchToProps)(_InvestmentsTable)
);

export default InvestmentsTable;
